/**
 * Label rendering — Canvas API, browser-side.
 * 300 DPI output for print quality.
 *
 * HORIZONTAL 75×20mm:
 *   Left content area (4 rows with black grid lines):
 *     Row 1: [EntityCode navy/colored | CategoryCode white] split by vertical line
 *     Row 2: AssetNumber bold centered
 *     Row 3: AssetName centered
 *     Row 4: "DO NOT REMOVE THIS LABEL" black bar white text
 *   Right: QR in black-bordered box spanning full height
 *
 * VERTICAL 20×30mm outer / 15×20mm inner:
 *   Outer box (black border): title area above inner box
 *     Top: "Rincian Aset:" bold + asset name
 *   Inner box (black border inside outer):
 *     QR code filling most of inner box
 *     Bottom bar: entity color bg + entity code white text
 */

const DPI = 300;
const MM = DPI / 25.4; // px per mm at 300 DPI ≈ 11.81

const BLACK = "#000000";
const WHITE = "#ffffff";
const DEFAULT_ENTITY_COLOR = "#0d1b4b"; // navy fallback

// ---------------------------------------------------------------------------
// Font loading — call loadFonts() once before rendering
// ---------------------------------------------------------------------------
let _fontsLoaded = false;

export async function loadFonts() {
  if (_fontsLoaded) return;

  // Commuter Sans Bold — commercial font, hosted in /public/fonts/
  const commuterSans = new FontFace(
    "Commuter Sans",
    "url(/fonts/CommuterSans-Bold.otf)",
    { weight: "700", style: "normal" },
  );

  // Poppins SemiBold + Light — Google Fonts
  const poppins600 = new FontFace(
    "Poppins",
    "url(https://fonts.gstatic.com/s/poppins/v22/pxiByp8kv8JHgFVrLEj6Z1xlFQ.woff2)",
    { weight: "600", style: "normal" },
  );

  const poppins400 = new FontFace(
    "Poppins",
    "url(https://fonts.gstatic.com/s/poppins/v22/pxiByp8kv8JHgFVrLDz8Z1xlFQ.woff2)",
    { weight: "400", style: "normal" },
  );

  const poppins300 = new FontFace(
    "Poppins",
    "url(https://fonts.gstatic.com/s/poppins/v22/pxiByp8kv8JHgFVrLDz8Z1xlFQ.woff2)",
    { weight: "300", style: "normal" },
  );

  try {
    const [cs, pp600, pp400, pp300] = await Promise.all([
      commuterSans.load(),
      poppins600.load(),
      poppins400.load(),
      poppins300.load(),
    ]);
    document.fonts.add(cs);
    document.fonts.add(pp600);
    document.fonts.add(pp400);
    document.fonts.add(pp300);
    _fontsLoaded = true;
  } catch (err) {
    console.warn(
      "[labelRenderer] Font load failed, falling back to system fonts:",
      err,
    );
    _fontsLoaded = true; // proceed with fallback
  }
}

// Font name helpers — use loaded custom fonts with system fallbacks
const FONT_CODE = (size) =>
  `bold ${size}px 'Commuter Sans', 'Arial Narrow', Arial, sans-serif`;
const FONT_BODY = (size) => `600 ${size}px 'Poppins', Arial, sans-serif`;
const FONT_MONO = (size) => `600 ${size}px 'Poppins', Arial, sans-serif`;

// Entity color comes directly from labelData.entityColor (per-entity, stored in DB)
function entityColor(labelData) {
  return labelData.entityColor || DEFAULT_ENTITY_COLOR;
}

// ---------------------------------------------------------------------------
// Shared: draw QR image onto context
// ---------------------------------------------------------------------------
function drawQR(ctx, qrDataUrl, x, y, w, h) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, x, y, w, h);
      resolve();
    };
    img.onerror = reject;
    img.src = qrDataUrl;
  });
}

// ---------------------------------------------------------------------------
// Shared: truncate text to fit maxWidth
// ---------------------------------------------------------------------------
function truncate(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (ctx.measureText(t + "…").width > maxWidth && t.length > 1) {
    t = t.slice(0, -1);
  }
  return t + "…";
}

// ---------------------------------------------------------------------------
// HORIZONTAL — 75×20mm
// ---------------------------------------------------------------------------
export async function renderHorizontalLabel(labelData) {
  const W = Math.round(75 * MM); // ≈ 886px  — outer box
  const H = Math.round(20 * MM); // ≈ 236px  — outer box

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // ── Outer box ──
  const OUTER_BORDER = 2;
  const PAD = Math.round(1.5 * MM); // 1.5mm padding from outer box to inner content

  // ── Inner content bounds ──
  const IX = PAD; // inner start X
  const IY = PAD; // inner start Y
  const IW = W - PAD * 2; // inner width
  const IH = H - PAD * 2; // inner height

  // ── QR section ──
  // QR grid box: square, fills inner height
  const QR_GAP = Math.round(1.5 * MM); // 2mm gap between info grid and QR grid box
  const QR_BOX_SIZE = IH; // QR outer grid box = inner height (square)
  const QR_INNER_PAD = Math.round(2 * MM); // 2mm padding inside QR grid box
  const QR_SIZE = QR_BOX_SIZE - QR_INNER_PAD * 1; // actual QR image size

  const QR_BOX_X = IX + IW - QR_BOX_SIZE - 1; // QR grid box starts here
  const INFO_W = QR_BOX_X - IX - QR_GAP; // left info grid width

  // ── Row heights (inside info grid) ──
  const GRID_BORDER = 2;
  const ROW1_H = Math.round(IH * 0.26); // entity | category
  const ROW4_H = Math.round(IH * 0.22); // DO NOT REMOVE
  const ROW23_H = Math.floor((IH - ROW1_H - ROW4_H) / 2);

  const ROW2_Y = IY + ROW1_H;
  const ROW3_Y = IY + ROW1_H + ROW23_H;
  const ROW4_Y = IY + IH - ROW4_H;

  // Visual center of each row between its border lines — prevents text hugging borders
  const GB = GRID_BORDER;
  const NUDGE = 4; // increase this number to push text lower
  const ROW1_CY = IY + GB + (ROW1_H - GB * 2) / 2 + NUDGE;
  const ROW2_CY = ROW2_Y + GB + (ROW23_H - GB * 2) / 2 + NUDGE;
  const ROW3_CY = ROW3_Y + GB + (ROW23_H - GB * 2) / 2 + NUDGE;
  const ROW4_CY = ROW4_Y + GB + (ROW4_H - GB * 2) / 2 + NUDGE;

  // Entity and category columns — equal width (50/50)
  const COL_W = Math.floor(INFO_W / 2);
  const CAT_X = IX + COL_W; // category column starts here

  // ── White base ──
  ctx.fillStyle = WHITE;
  ctx.fillRect(0, 0, W, H);

  // ── Row 1 Left: Entity code — colored bg ──
  ctx.fillStyle = entityColor(labelData);
  ctx.fillRect(IX, IY, COL_W, ROW1_H);
  ctx.fillStyle = WHITE;
  ctx.font = FONT_CODE(Math.round(ROW1_H * 0.58));
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(labelData.entityCode, IX + COL_W / 2, ROW1_CY);

  // ── Row 1 Right: Category code — white bg, black text ──
  ctx.fillStyle = WHITE;
  ctx.fillRect(CAT_X, IY, INFO_W - COL_W, ROW1_H);
  ctx.fillStyle = BLACK;
  ctx.font = FONT_CODE(Math.round(ROW1_H * 0.58));
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(labelData.categoryCode, CAT_X + (INFO_W - COL_W) / 2, ROW1_CY);

  // ── Row 2: Asset number ──
  ctx.fillStyle = WHITE;
  ctx.fillRect(IX, ROW2_Y, INFO_W, ROW23_H);
  ctx.fillStyle = BLACK;
  ctx.font = FONT_MONO(Math.round(ROW23_H * 0.55));
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(
    truncate(ctx, labelData.assetNumber, INFO_W - 16),
    IX + INFO_W / 2,
    ROW2_CY,
  );

  // ── Row 3: Asset name ──
  ctx.fillStyle = WHITE;
  ctx.fillRect(IX, ROW3_Y, INFO_W, ROW23_H);
  ctx.fillStyle = BLACK;
  ctx.font = FONT_BODY(Math.round(ROW23_H * 0.46));
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(
    truncate(ctx, labelData.assetName, INFO_W - 16),
    IX + INFO_W / 2,
    ROW3_CY,
  );

  // ── Row 4: DO NOT REMOVE bar ──
  ctx.fillStyle = BLACK;
  ctx.fillRect(IX, ROW4_Y, INFO_W, ROW4_H);
  ctx.fillStyle = WHITE;
  ctx.font = FONT_BODY(Math.round(ROW4_H * 0.52));
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("DO NOT REMOVE THIS LABEL", IX + INFO_W / 2, ROW4_CY);

  // ── Info grid black lines ──
  ctx.fillStyle = BLACK;
  // Outer border of info grid
  ctx.fillRect(IX, IY, INFO_W, GRID_BORDER); // top
  ctx.fillRect(IX, IY + IH - GRID_BORDER, INFO_W, GRID_BORDER); // bottom (ROW4 fills this)
  ctx.fillRect(IX, IY, GRID_BORDER, IH); // left
  ctx.fillRect(IX + INFO_W - GRID_BORDER, IY, GRID_BORDER, IH); // right
  // Horizontal row dividers
  ctx.fillRect(IX, ROW2_Y, INFO_W, GRID_BORDER); // row1/row2
  ctx.fillRect(IX, ROW3_Y, INFO_W, GRID_BORDER); // row2/row3
  ctx.fillRect(IX, ROW4_Y, INFO_W, GRID_BORDER); // row3/row4
  // Vertical divider in row 1 (equal 50/50)
  ctx.fillRect(CAT_X, IY, GRID_BORDER, ROW1_H);

  // ── QR grid box (black border, 2mm inner padding) ──
  const QR_BOX_Y = IY;
  ctx.strokeStyle = BLACK;
  ctx.lineWidth = GRID_BORDER;
  ctx.strokeRect(
    QR_BOX_X + GRID_BORDER / 2,
    QR_BOX_Y + GRID_BORDER / 2,
    QR_BOX_SIZE - GRID_BORDER,
    QR_BOX_SIZE - GRID_BORDER,
  );

  // QR image inside grid box with 2mm padding
  ctx.fillStyle = WHITE;
  ctx.fillRect(
    QR_BOX_X + GRID_BORDER,
    QR_BOX_Y + GRID_BORDER,
    QR_BOX_SIZE - GRID_BORDER * 2,
    QR_BOX_SIZE - GRID_BORDER * 2,
  );

  await drawQR(
    ctx,
    labelData.qrDataUrl,
    QR_BOX_X + QR_INNER_PAD - 1 * MM,
    QR_BOX_Y + QR_INNER_PAD - 1 * MM,
    QR_SIZE,
    QR_SIZE,
  );

  // ── Outer border of entire label ──
  ctx.strokeStyle = BLACK;
  ctx.lineWidth = OUTER_BORDER;
  ctx.strokeRect(
    OUTER_BORDER / 2,
    OUTER_BORDER / 2,
    W - OUTER_BORDER,
    H - OUTER_BORDER,
  );

  return canvas.toDataURL("image/jpeg", 0.95);
}

// ---------------------------------------------------------------------------
// VERTICAL — 20×30mm outer box, 15×20mm inner box
// ---------------------------------------------------------------------------
export async function renderVerticalLabel(labelData) {
  const OUTER_W = Math.round(20 * MM); // ≈ 236px
  const OUTER_H = Math.round(30 * MM); // ≈ 354px
  const INNER_W = Math.round(15 * MM); // ≈ 177px
  const INNER_H = Math.round(20 * MM); // ≈ 236px

  const canvas = document.createElement("canvas");
  canvas.width = OUTER_W;
  canvas.height = OUTER_H;
  const ctx = canvas.getContext("2d");

  const BORDER = 3;
  const BOTTOM_MARGIN = Math.round(1.5 * MM); // 2mm gap: bottom of inner box to outer box bottom
  const INNER_X = Math.round((OUTER_W - INNER_W) / 2);
  const INNER_Y = OUTER_H - INNER_H - BOTTOM_MARGIN - BORDER;

  // ── White background ──
  ctx.fillStyle = WHITE;
  ctx.fillRect(0, 0, OUTER_W, OUTER_H);

  // ── Outer box border ──
  ctx.strokeStyle = BLACK;
  ctx.lineWidth = BORDER;
  ctx.strokeRect(BORDER / 2, BORDER / 2, OUTER_W - BORDER, OUTER_H - BORDER);

  // ── Top area (above inner box): "Rincian Aset:" + asset name ──
  const TOP_MARGIN = Math.round(1.5 * MM); // 3mm from outer box top to title text
  const titleSize = Math.round(14 * MM * 0.18); // proportional to label width
  const nameSize = Math.round(14 * (MM / 25.4) * 14); // 14pt equivalent at 300dpi ≈ 16px... use fixed
  // Fixed readable sizes at 300 DPI
  const TITLE_PX = Math.round(MM * 1.7); // ~33px — "Rincian Aset:" bold
  const NAME_PX = Math.round(MM * 1.2); // ~26px — asset name, thin regular

  ctx.fillStyle = BLACK;

  // "Rincian Aset:" — Poppins SemiBold
  ctx.font = `600 ${TITLE_PX}px 'Poppins', Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("Rincian Aset:", OUTER_W / 2, BORDER + TOP_MARGIN);

  // Asset name — Poppins Regular (300 weight = thin), max 2 lines, centered
  ctx.font = `300 ${NAME_PX}px 'Poppins', Arial, sans-serif`;
  const maxNameW = OUTER_W - BORDER * 2 - Math.round(2 * MM);
  const nameStartY = BORDER + TOP_MARGIN + TITLE_PX + Math.round(0.5 * MM);

  // Word-wrap to exactly 2 lines
  const words = labelData.assetName.split(" ");
  let line1 = "",
    line2 = "";
  for (const word of words) {
    const test = line1 ? line1 + " " + word : word;
    if (ctx.measureText(test).width <= maxNameW) {
      line1 = test;
    } else {
      // Overflow goes to line 2 — truncate if still too long
      const test2 = line2 ? line2 + " " + word : word;
      if (ctx.measureText(test2).width <= maxNameW) {
        line2 = test2;
      } else {
        // Truncate line 2
        let t = line2 + " " + word;
        while (ctx.measureText(t + "…").width > maxNameW && t.length > 1) {
          t = t.slice(0, -1);
        }
        line2 = t + "…";
      }
    }
  }

  ctx.fillText(line1, OUTER_W / 2, nameStartY);
  if (line2)
    ctx.fillText(
      line2,
      OUTER_W / 2,
      nameStartY + NAME_PX + Math.round(0.5 * MM),
    );

  // ── Inner box border ──
  ctx.strokeStyle = BLACK;
  ctx.lineWidth = BORDER;
  ctx.strokeRect(
    INNER_X + BORDER / 2,
    INNER_Y + BORDER / 2,
    INNER_W - BORDER,
    INNER_H - BORDER,
  );

  // ── Inside inner box: QR centered + entity bar ──
  const ENT_BAR_H = Math.round(INNER_H * 0.22);
  const QR_TOP_GAP = Math.round(1.5 * MM); // gap from inner box top border to QR
  const QR_BOT_GAP = Math.round(1.5 * MM); // gap from QR bottom to entity bar top

  // Available vertical space for QR (between inner top and entity bar)
  const QR_AVAILABLE_H =
    INNER_H - BORDER * 2 - ENT_BAR_H - QR_TOP_GAP - QR_BOT_GAP;
  // QR is square — size constrained by both available height and inner width minus padding
  const QR_MAX_W = INNER_W - BORDER * 2 - Math.round(1.5 * MM) * 1;
  const QR_SIZE = Math.min(QR_AVAILABLE_H, QR_MAX_W);

  // Center QR horizontally and vertically in the available space
  const QR_X = INNER_X + (INNER_W - QR_SIZE) / 2;
  const QR_SPACE_TOP = INNER_Y + BORDER + QR_TOP_GAP;
  const QR_SPACE_BOTTOM = INNER_Y + INNER_H - BORDER - ENT_BAR_H - QR_BOT_GAP;
  const QR_Y = QR_SPACE_TOP + (QR_SPACE_BOTTOM - QR_SPACE_TOP - QR_SIZE) / 2;

  await drawQR(ctx, labelData.qrDataUrl, QR_X, QR_Y, QR_SIZE, QR_SIZE);

  // ── Entity bar (inside inner box, bottom) ──
  const ENT_BAR_Y = INNER_Y + INNER_H - ENT_BAR_H - BORDER;
  ctx.fillStyle = entityColor(labelData);
  ctx.fillRect(INNER_X + BORDER, ENT_BAR_Y, INNER_W - BORDER * 2, ENT_BAR_H);
  ctx.fillStyle = WHITE;
  ctx.font = FONT_CODE(Math.round(ENT_BAR_H * 0.58));
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(
    labelData.entityCode,
    INNER_X + INNER_W / 2,
    ENT_BAR_Y + ENT_BAR_H / 2,
  );

  return canvas.toDataURL("image/jpeg", 0.95);
}

// ---------------------------------------------------------------------------
// Print sheet — arrange labels on A4 or A3
// ---------------------------------------------------------------------------
export async function renderPrintSheet(
  labels,
  pageSize = "A4",
  format = "horizontal",
) {
  const PAGE = {
    A4: { w: Math.round(210 * MM), h: Math.round(297 * MM) },
    A3: { w: Math.round(297 * MM), h: Math.round(420 * MM) },
  }[pageSize];

  const MARGIN = Math.round(10 * MM);
  const GAP = Math.round(3 * MM);

  const LABEL_SIZE = {
    horizontal: { w: Math.round(75 * MM), h: Math.round(20 * MM) },
    vertical: { w: Math.round(20 * MM), h: Math.round(30 * MM) }, // outer box
  };

  const canvas = document.createElement("canvas");
  canvas.width = PAGE.w;
  canvas.height = PAGE.h;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = WHITE;
  ctx.fillRect(0, 0, PAGE.w, PAGE.h);

  // When format === 'both', render ALL horizontals first then ALL verticals.
  // This way cuts can be made in one pass per format — much neater.
  const formats = format === "both" ? ["horizontal", "vertical"] : [format];

  async function placeLabels(fmt, startY) {
    let curX = MARGIN;
    let curY = startY;
    let rowH = 0;

    const lw = LABEL_SIZE[fmt].w;
    const lh = LABEL_SIZE[fmt].h;

    for (const label of labels) {
      if (curX + lw > PAGE.w - MARGIN) {
        curX = MARGIN;
        curY += rowH + GAP;
        rowH = 0;
      }
      if (curY + lh > PAGE.h - MARGIN) break;

      const dataUrl =
        fmt === "horizontal"
          ? await renderHorizontalLabel(label)
          : await renderVerticalLabel(label);

      await new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, curX, curY, lw, lh);
          res();
        };
        img.onerror = rej;
        img.src = dataUrl;
      });

      curX += lw + GAP;
      rowH = Math.max(rowH, lh);
    }

    // Return the Y position after last row so next format section starts below
    return curY + rowH;
  }

  let startY = MARGIN;
  let finalEndY = MARGIN;

  for (let i = 0; i < formats.length; i++) {
    const fmt = formats[i];
    const endY = await placeLabels(fmt, startY);
    finalEndY = endY;

    if (formats.length > 1 && i < formats.length - 1) {
      ctx.save();
      ctx.setLineDash([8, 6]);
      ctx.strokeStyle = "#cbd5e1";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(MARGIN, endY + GAP);
      ctx.lineTo(PAGE.w - MARGIN, endY + GAP);
      ctx.stroke();
      ctx.restore();
      startY = endY + GAP * 3;
    }
  }

  // Crop to actual content height — prevents blank second page when printing
  const usedH = Math.min(finalEndY + MARGIN, PAGE.h);
  const cropped = document.createElement("canvas");
  cropped.width = PAGE.w;
  cropped.height = usedH;
  cropped.getContext("2d").drawImage(canvas, 0, 0);

  return cropped;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
export function downloadCanvasAsJpeg(canvas, filename) {
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/jpeg", 0.95);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function printCanvas(canvas) {
  const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
  const win = window.open("", "_blank");
  win.document.write(`<!DOCTYPE html><html><head><title>Print Labels</title>
    <style>@page{margin:0;size:auto;}body{margin:0;padding:0;}img{display:block;width:100%;}</style>
    </head><body><img src="${dataUrl}" onload="window.print();window.close();"/></body></html>`);
  win.document.close();
}
